/**
 * VideoSplitter - Home View Controller Module (TypeScript)
 * Full Parity with ConnecTab APK Platform & Surface Architecture
 */

import { SplitterEngine, SplitResultClip, ProgressInfo } from '../splitter-engine';

interface PlatformSurfaceConfig {
  id: string;
  label: string;
  maxSeconds: number;
  maxBytes: number;
  aspectRatio: string;
  strategy: string;
  desc: string;
}

interface PlatformConfig {
  label: string;
  surfaces: PlatformSurfaceConfig[];
}

export class HomeView {

  private static videoFile: File | null = null;
  private static videoUrl: string | null = null;
  private static videoDuration = 0;
  private static videoSizeBytes = 0;
  private static isSplitting = false;
  private static splitClips: SplitResultClip[] = [];

  private static selectedPlatform = 'instagram';
  private static selectedSurfaceId = 'story';
  private static clipDurationSec = 60;
  private static partCount = 2;
  private static splitMode: 'duration' | 'parts' = 'duration';
  private static useTimeRange = false;
  private static startTime = 0;
  private static endTime = 0;

  private static PLATFORM_CONFIGS: Record<string, PlatformConfig> = {
    'instagram': {
      label: 'Instagram',
      surfaces: [
        {
          id: 'story',
          label: 'Instagram Story',
          maxSeconds: 60,
          maxBytes: 100000000,
          aspectRatio: '9:16 Vertical',
          strategy: 'Duration Only',
          desc: '• Max clip duration: 60 seconds<br>• Aspect ratio: 9:16 Vertical<br>• Soft size target: ~100 MB per clip'
        },
        {
          id: 'reel',
          label: 'Instagram Reel',
          maxSeconds: 90,
          maxBytes: 200000000,
          aspectRatio: '9:16 Vertical',
          strategy: 'Adaptive',
          desc: '• Max clip duration: 90 seconds<br>• Aspect ratio: 9:16 Vertical<br>• Target size allowance: ~200 MB per clip'
        },
        {
          id: 'feed',
          label: 'Instagram Feed',
          maxSeconds: 60,
          maxBytes: 100000000,
          aspectRatio: '1:1 Square',
          strategy: 'Duration + Size Target',
          desc: '• Max clip duration: 60 seconds<br>• Aspect ratio: 1:1 Square / 4:5 Feed<br>• Soft size target: ~100 MB per clip'
        }
      ]
    },
    'tiktok': {
      label: 'TikTok',
      surfaces: [
        {
          id: 'default',
          label: 'TikTok Video',
          maxSeconds: 180,
          maxBytes: 300000000,
          aspectRatio: '9:16 Vertical',
          strategy: 'Duration + Size Target',
          desc: '• Max clip duration: 180 seconds<br>• Aspect ratio: 9:16 Vertical<br>• Recommended resolution: 1080x1920'
        }
      ]
    },
    'whatsapp': {
      label: 'WhatsApp',
      surfaces: [
        {
          id: 'status',
          label: 'WhatsApp Status',
          maxSeconds: 30,
          maxBytes: 16000000,
          aspectRatio: 'Mobile 720p',
          strategy: 'Size & Time Limit',
          desc: '• Max clip duration: 30 seconds<br>• Hard attachment size limit: ~16 MB per clip<br>• Resolution: Standard 720p Mobile'
        }
      ]
    },
    'youtube': {
      label: 'YouTube',
      surfaces: [
        {
          id: 'shorts',
          label: 'YouTube Shorts',
          maxSeconds: 60,
          maxBytes: 1000000000,
          aspectRatio: '9:16 Vertical',
          strategy: 'Duration Only',
          desc: '• Max clip duration: 60 seconds<br>• Aspect ratio: 9:16 Vertical<br>• High-bitrate encoding'
        }
      ]
    },
    'twitter': {
      label: 'Twitter / X',
      surfaces: [
        {
          id: 'default',
          label: 'Twitter Post',
          maxSeconds: 140,
          maxBytes: 512000000,
          aspectRatio: 'HD Video',
          strategy: 'Duration + Size Target',
          desc: '• Max clip duration: 140 seconds<br>• Maximum file size limit: 512 MB per clip<br>• Format: H.264 / MP4 stream'
        }
      ]
    },
    'telegram': {
      label: 'Telegram',
      surfaces: [
        {
          id: 'free',
          label: 'Telegram Free',
          maxSeconds: 0,
          maxBytes: 2000000000,
          aspectRatio: 'Original',
          strategy: 'Size Only (2GB Limit)',
          desc: '• Max clip duration: Unlimited (Split by File Size)<br>• File size limit: 2.0 GB per part'
        },
        {
          id: 'premium',
          label: 'Telegram Premium',
          maxSeconds: 0,
          maxBytes: 4000000000,
          aspectRatio: 'Original',
          strategy: 'Size Only (4GB Limit)',
          desc: '• Max clip duration: Unlimited (Split by File Size)<br>• File size limit: 4.0 GB per part'
        }
      ]
    },
    'snapchat': {
      label: 'Snapchat',
      surfaces: [
        {
          id: 'default',
          label: 'Snapchat Story',
          maxSeconds: 60,
          maxBytes: 2000000000,
          aspectRatio: 'Mobile Portrait',
          strategy: 'Duration Only',
          desc: '• Max clip duration: 60 seconds<br>• Format: Mobile Portrait Stream<br>• Generous size allowance'
        }
      ]
    },
    'discord': {
      label: 'Discord',
      surfaces: [
        {
          id: 'free',
          label: 'Discord Free',
          maxSeconds: 60,
          maxBytes: 10000000,
          aspectRatio: 'Compressed',
          strategy: 'Duration + Size Target (10MB)',
          desc: '• Max clip duration: 60 seconds<br>• Upload size limit: 10 MB per clip'
        },
        {
          id: 'nitro',
          label: 'Discord Nitro',
          maxSeconds: 60,
          maxBytes: 500000000,
          aspectRatio: 'HD Video',
          strategy: 'Duration + Size Target (500MB)',
          desc: '• Max clip duration: 60 seconds<br>• Upload size limit: 500 MB per clip'
        },
        {
          id: 'nitro_basic',
          label: 'Discord Nitro Basic',
          maxSeconds: 60,
          maxBytes: 1000000000,
          aspectRatio: 'HD Video',
          strategy: 'Duration + Size Target (1GB)',
          desc: '• Max clip duration: 60 seconds<br>• Upload size limit: 1.0 GB per clip'
        }
      ]
    },
    'reddit': {
      label: 'Reddit',
      surfaces: [
        {
          id: 'default',
          label: 'Reddit Video',
          maxSeconds: 15,
          maxBytes: 1000000000,
          aspectRatio: 'Original',
          strategy: 'Duration Only (15s)',
          desc: '• Max clip duration: 15 seconds<br>• Subreddit video upload standard'
        }
      ]
    },
    'custom': {
      label: 'Custom',
      surfaces: [
        {
          id: 'custom',
          label: 'Custom Split',
          maxSeconds: 30,
          maxBytes: 0,
          aspectRatio: 'Custom',
          strategy: 'User Defined',
          desc: '• Set custom duration per clip or split into equal parts'
        }
      ]
    }
  };

  // DOM Elements
  private static mainConverterCard: HTMLElement;
  private static initialWorkspaceWrap: HTMLElement;
  private static expandedWorkspaceWrap: HTMLElement;

  private static tabUpload: HTMLButtonElement;
  private static tabUrl: HTMLButtonElement;
  private static dropzoneBox: HTMLElement;
  private static btnChooseFileInner: HTMLButtonElement;
  private static urlBox: HTMLElement;
  private static videoUrlInput: HTMLInputElement;
  private static loadUrlBtn: HTMLButtonElement;
  private static videoFileInput: HTMLInputElement;
  private static videoPlayer: HTMLVideoElement;

  private static platformChipsWrap: HTMLElement;
  private static surfaceChipsWrap: HTMLElement;
  private static surfaceSectionGroup: HTMLElement;

  private static customSplitGroup: HTMLElement;
  private static modeSecBtn: HTMLButtonElement;
  private static modePartsBtn: HTMLButtonElement;

  private static customStepperLabel: HTMLElement;
  private static customStepperVal: HTMLElement;
  private static customStepMinus: HTMLButtonElement;
  private static customStepPlus: HTMLButtonElement;

  private static presetInfoTitle: HTMLElement;
  private static presetInfoBody: HTMLElement;

  private static timeRangeToggle: HTMLInputElement;
  private static rangeSliderGroup: HTMLElement;
  private static rangeValText: HTMLElement;
  private static startTimeSlider: HTMLInputElement;
  private static endTimeSlider: HTMLInputElement;

  private static fileInfoName: HTMLElement;
  private static fileInfoMeta: HTMLElement;
  private static fileInfoCloseBtn: HTMLButtonElement;
  private static splitOverviewText: HTMLElement;

  private static extractBtn: HTMLButtonElement;
  private static extractBtnText: HTMLElement;

  private static resultsContent: HTMLElement;
  private static progressBarFill: HTMLElement;
  private static progressText: HTMLElement;
  private static clipsGrid: HTMLElement;
  private static downloadZipBtn: HTMLButtonElement;
  private static downloadDirectBtn: HTMLButtonElement;
  private static shareClipsBtn: HTMLButtonElement;
  private static clearClipsBtn: HTMLButtonElement;

  public static init(): void {
    (window as any).__TS_HOMEVIEW_INIT__ = true;
    this.cacheDom();
    this.bindEvents();
    this.bindFaqAccordion();
    this.renderPlatformChips();
    this.renderSurfaceChips('instagram');
  }

  private static cacheDom(): void {
    this.mainConverterCard = document.getElementById('main-converter-card') as HTMLElement;
    this.initialWorkspaceWrap = document.getElementById('initial-workspace-wrap') as HTMLElement;
    this.expandedWorkspaceWrap = document.getElementById('expanded-workspace-wrap') as HTMLElement;

    this.tabUpload = document.getElementById('tab-upload') as HTMLButtonElement;
    this.tabUrl = document.getElementById('tab-url') as HTMLButtonElement;
    this.dropzoneBox = document.getElementById('dropzone-box') as HTMLElement;
    this.btnChooseFileInner = document.getElementById('btn-choose-file-inner') as HTMLButtonElement;
    this.urlBox = document.getElementById('url-box') as HTMLElement;
    this.videoUrlInput = document.getElementById('video-url-input') as HTMLInputElement;
    this.loadUrlBtn = document.getElementById('load-url-btn') as HTMLButtonElement;
    this.videoFileInput = document.getElementById('video-file-input') as HTMLInputElement;
    this.videoPlayer = document.getElementById('video-player') as HTMLVideoElement;

    this.platformChipsWrap = document.getElementById('platform-chips-wrap') as HTMLElement;
    this.surfaceChipsWrap = document.getElementById('surface-chips-wrap') as HTMLElement;
    this.surfaceSectionGroup = document.getElementById('surface-section-group') as HTMLElement;

    this.customSplitGroup = document.getElementById('custom-split-group') as HTMLElement;
    this.modeSecBtn = document.getElementById('mode-sec-btn') as HTMLButtonElement;
    this.modePartsBtn = document.getElementById('mode-parts-btn') as HTMLButtonElement;

    this.customStepperLabel = document.getElementById('custom-stepper-label') as HTMLElement;
    this.customStepperVal = document.getElementById('custom-stepper-val') as HTMLElement;
    this.customStepMinus = document.getElementById('custom-step-minus') as HTMLButtonElement;
    this.customStepPlus = document.getElementById('custom-step-plus') as HTMLButtonElement;

    this.presetInfoTitle = document.getElementById('preset-info-title') as HTMLElement;
    this.presetInfoBody = document.getElementById('preset-info-body') as HTMLElement;

    this.timeRangeToggle = document.getElementById('time-range-toggle') as HTMLInputElement;
    this.rangeSliderGroup = document.getElementById('range-slider-group') as HTMLElement;
    this.rangeValText = document.getElementById('range-val-text') as HTMLElement;
    this.startTimeSlider = document.getElementById('start-time-slider') as HTMLInputElement;
    this.endTimeSlider = document.getElementById('end-time-slider') as HTMLInputElement;

    this.fileInfoName = document.getElementById('file-info-name') as HTMLElement;
    this.fileInfoMeta = document.getElementById('file-info-meta') as HTMLElement;
    this.fileInfoCloseBtn = document.getElementById('file-info-close-btn') as HTMLButtonElement;
    this.splitOverviewText = document.getElementById('split-overview-text') as HTMLElement;

    this.extractBtn = document.getElementById('extract-btn') as HTMLButtonElement;
    this.extractBtnText = document.getElementById('extract-btn-text') as HTMLElement;

    this.resultsContent = document.getElementById('results-content') as HTMLElement;
    this.progressBarFill = document.getElementById('progress-bar-fill') as HTMLElement;
    this.progressText = document.getElementById('progress-text') as HTMLElement;
    this.clipsGrid = document.getElementById('clips-grid') as HTMLElement;
    this.downloadZipBtn = document.getElementById('download-zip-btn') as HTMLButtonElement;
    this.downloadDirectBtn = document.getElementById('download-direct-btn') as HTMLButtonElement;
    this.shareClipsBtn = document.getElementById('share-clips-btn') as HTMLButtonElement;
    this.clearClipsBtn = document.getElementById('clear-clips-btn') as HTMLButtonElement;
  }

  private static formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(mins)}:${pad(secs)}`;
  }

  private static formatBytes(bytes: number): string {
    if (!bytes || bytes <= 0) return '0.0 MB';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  }

  private static getActiveSurfaceObj(): PlatformSurfaceConfig {
    const pConfig = this.PLATFORM_CONFIGS[this.selectedPlatform] || this.PLATFORM_CONFIGS['instagram'];
    const surface = pConfig.surfaces.find(s => s.id === this.selectedSurfaceId) || pConfig.surfaces[0];
    return surface;
  }

  private static updateOverviewText(): void {
    let dur = this.videoDuration;
    if (this.useTimeRange && this.endTime > this.startTime) {
      dur = this.endTime - this.startTime;
    }

    const currentSurface = this.getActiveSurfaceObj();
    let clipCount = 1;

    if (this.selectedPlatform === 'custom') {
      if (this.splitMode === 'parts') {
        clipCount = Math.max(1, this.partCount);
        const segLen = (dur / clipCount).toFixed(1);
        if (this.splitOverviewText) {
          this.splitOverviewText.textContent = `Your video (${this.formatTime(dur)}) will be split into ${clipCount} equal clips of ~${segLen}s each.`;
        }
      } else {
        const len = Math.max(1, this.clipDurationSec);
        clipCount = Math.ceil(dur / len);
        if (this.splitOverviewText) {
          this.splitOverviewText.textContent = `Your video (${this.formatTime(dur)}) will be split into ${clipCount} clip${clipCount > 1 ? 's' : ''} of ${len} seconds each.`;
        }
      }
      return;
    }

    const maxSec = currentSurface.maxSeconds;
    const maxB = currentSurface.maxBytes;

    if (maxSec > 0) {
      clipCount = Math.ceil(dur / maxSec);
      if (this.splitOverviewText) {
        this.splitOverviewText.textContent = `Your video (${this.formatTime(dur)}) will be split into ${clipCount} clip${clipCount > 1 ? 's' : ''} for ${currentSurface.label} (${maxSec}s max per clip).`;
      }
    } else if (maxB > 0 && this.videoSizeBytes > 0) {
      clipCount = Math.ceil(this.videoSizeBytes / maxB);
      if (this.splitOverviewText) {
        this.splitOverviewText.textContent = `Your video (${this.formatTime(dur)}) will be split into ${clipCount} part${clipCount > 1 ? 's' : ''} targeting ${this.formatBytes(maxB)} limit per part.`;
      }
    } else {
      if (this.splitOverviewText) {
        this.splitOverviewText.textContent = `Your video (${this.formatTime(dur)}) is ready for ${currentSurface.label} processing.`;
      }
    }
  }

  private static renderPlatformChips(): void {
    if (!this.platformChipsWrap) return;
    this.platformChipsWrap.innerHTML = '';

    Object.keys(this.PLATFORM_CONFIGS).forEach(pKey => {
      const pConfig = this.PLATFORM_CONFIGS[pKey];
      const btn = document.createElement('button');
      btn.className = `chip-item ${pKey === this.selectedPlatform ? 'active' : ''}`;
      btn.setAttribute('data-platform', pKey);
      btn.textContent = pConfig.label;

      btn.addEventListener('click', () => {
        const allBtns = this.platformChipsWrap.querySelectorAll('.chip-item');
        allBtns.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');

        this.selectedPlatform = pKey;
        const newSurfaces = this.PLATFORM_CONFIGS[this.selectedPlatform].surfaces;
        this.selectedSurfaceId = newSurfaces[0].id;
        this.clipDurationSec = newSurfaces[0].maxSeconds || 30;

        this.renderSurfaceChips(this.selectedPlatform);
        this.updateSurfaceInfoBox();
        this.updateOverviewText();
      });

      this.platformChipsWrap.appendChild(btn);
    });
  }

  private static renderSurfaceChips(platformKey: string): void {
    if (!this.surfaceChipsWrap || !this.surfaceSectionGroup) return;
    this.surfaceChipsWrap.innerHTML = '';

    const pConfig = this.PLATFORM_CONFIGS[platformKey] || this.PLATFORM_CONFIGS['instagram'];
    const surfaces = pConfig.surfaces;

    if (platformKey === 'custom') {
      this.surfaceSectionGroup.style.display = 'none';
      if (this.customSplitGroup) this.customSplitGroup.style.display = 'block';
      return;
    } else {
      if (this.customSplitGroup) this.customSplitGroup.style.display = 'none';
    }

    if (surfaces.length <= 1) {
      this.surfaceSectionGroup.style.display = 'none';
    } else {
      this.surfaceSectionGroup.style.display = 'block';
    }

    surfaces.forEach(sObj => {
      const btn = document.createElement('button');
      btn.className = `chip-item ${sObj.id === this.selectedSurfaceId ? 'active' : ''}`;
      btn.setAttribute('data-surface', sObj.id);
      btn.textContent = sObj.label;

      btn.addEventListener('click', () => {
        const allBtns = this.surfaceChipsWrap.querySelectorAll('.chip-item');
        allBtns.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');

        this.selectedSurfaceId = sObj.id;
        this.clipDurationSec = sObj.maxSeconds || 30;

        this.updateSurfaceInfoBox();
        this.updateOverviewText();
      });

      this.surfaceChipsWrap.appendChild(btn);
    });
  }

  private static updateSurfaceInfoBox(): void {
    const sObj = this.getActiveSurfaceObj();
    if (this.presetInfoTitle) this.presetInfoTitle.textContent = sObj.label;
    if (this.presetInfoBody) this.presetInfoBody.innerHTML = sObj.desc;
  }

  private static bindEvents(): void {
    if (!this.dropzoneBox) return;

    this.tabUpload.addEventListener('click', (e) => {
      e.preventDefault();
      this.tabUpload.classList.add('active');
      this.tabUrl.classList.remove('active');
      this.dropzoneBox.style.display = 'flex';
      this.urlBox.style.display = 'none';
    });

    this.tabUrl.addEventListener('click', (e) => {
      e.preventDefault();
      this.tabUrl.classList.add('active');
      this.tabUpload.classList.remove('active');
      this.urlBox.style.display = 'block';
      this.dropzoneBox.style.display = 'none';
    });

    this.dropzoneBox.addEventListener('click', () => this.videoFileInput.click());
    if (this.btnChooseFileInner) {
      this.btnChooseFileInner.addEventListener('click', (e) => {
        e.stopPropagation();
        this.videoFileInput.click();
      });
    }

    this.dropzoneBox.addEventListener('dragover', (e) => {
      e.preventDefault();
      this.dropzoneBox.classList.add('dragover');
    });
    this.dropzoneBox.addEventListener('dragleave', () => this.dropzoneBox.classList.remove('dragover'));
    this.dropzoneBox.addEventListener('drop', (e) => {
      e.preventDefault();
      this.dropzoneBox.classList.remove('dragover');
      if (e.dataTransfer?.files && e.dataTransfer.files[0]) {
        this.handleFileSelection(e.dataTransfer.files[0]);
      }
    });

    this.videoFileInput.addEventListener('change', (e) => {
      const target = e.target as HTMLInputElement;
      if (target.files && target.files[0]) {
        this.handleFileSelection(target.files[0]);
      }
    });

    this.loadUrlBtn.addEventListener('click', () => {
      const url = this.videoUrlInput.value.trim();
      if (!url) {
        alert('Please enter a valid video URL.');
        return;
      }
      this.handleUrlLoad(url);
    });

    if (this.fileInfoCloseBtn) {
      this.fileInfoCloseBtn.addEventListener('click', () => this.resetToInitialWorkspace());
    }

    if (this.modeSecBtn && this.modePartsBtn) {
      this.modeSecBtn.addEventListener('click', () => {
        this.modeSecBtn.classList.add('active');
        this.modePartsBtn.classList.remove('active');
        this.splitMode = 'duration';
        this.customStepperLabel.textContent = 'Seconds per clip';
        this.customStepperVal.textContent = `${this.clipDurationSec}s`;
        this.updateOverviewText();
      });

      this.modePartsBtn.addEventListener('click', () => {
        this.modePartsBtn.classList.add('active');
        this.modeSecBtn.classList.remove('active');
        this.splitMode = 'parts';
        this.customStepperLabel.textContent = 'Number of equal clips';
        this.customStepperVal.textContent = `${this.partCount}`;
        this.updateOverviewText();
      });
    }

    if (this.customStepMinus) {
      this.customStepMinus.addEventListener('click', () => {
        if (this.splitMode === 'parts') {
          this.partCount = Math.max(2, this.partCount - 1);
          this.customStepperVal.textContent = `${this.partCount}`;
        } else {
          this.clipDurationSec = Math.max(5, this.clipDurationSec - 5);
          this.customStepperVal.textContent = `${this.clipDurationSec}s`;
        }
        this.updateOverviewText();
      });
    }

    if (this.customStepPlus) {
      this.customStepPlus.addEventListener('click', () => {
        if (this.splitMode === 'parts') {
          this.partCount = Math.min(20, this.partCount + 1);
          this.customStepperVal.textContent = `${this.partCount}`;
        } else {
          this.clipDurationSec = Math.min(600, this.clipDurationSec + 5);
          this.customStepperVal.textContent = `${this.clipDurationSec}s`;
        }
        this.updateOverviewText();
      });
    }

    this.timeRangeToggle.addEventListener('change', (e) => {
      const target = e.target as HTMLInputElement;
      this.useTimeRange = target.checked;
      this.rangeSliderGroup.style.display = this.useTimeRange ? 'block' : 'none';
      this.updateOverviewText();
    });

    this.startTimeSlider.addEventListener('input', () => {
      let sVal = parseFloat(this.startTimeSlider.value);
      let eVal = parseFloat(this.endTimeSlider.value);
      if (sVal >= eVal) {
        sVal = Math.max(0, eVal - 0.1);
        this.startTimeSlider.value = sVal.toString();
      }
      this.startTime = sVal;
      this.updateRangeDisplay();
      this.updateOverviewText();
    });

    this.endTimeSlider.addEventListener('input', () => {
      let sVal = parseFloat(this.startTimeSlider.value);
      let eVal = parseFloat(this.endTimeSlider.value);
      if (eVal <= sVal) {
        eVal = Math.min(this.videoDuration, sVal + 0.1);
        this.endTimeSlider.value = eVal.toString();
      }
      this.endTime = eVal;
      this.updateRangeDisplay();
      this.updateOverviewText();
    });

    this.extractBtn.addEventListener('click', () => this.startSplitting());
    if (this.clearClipsBtn) this.clearClipsBtn.addEventListener('click', () => this.clearOutputState());
    if (this.downloadZipBtn) this.downloadZipBtn.addEventListener('click', () => this.downloadAllZip());
    if (this.downloadDirectBtn) this.downloadDirectBtn.addEventListener('click', () => this.downloadAllDirectFiles());
    if (this.shareClipsBtn) this.shareClipsBtn.addEventListener('click', () => this.shareAllClips());
  }

  private static handleFileSelection(file: File): void {
    if (!file.type.startsWith('video/') && !/\.(mp4|mov|avi|webm|mkv)$/i.test(file.name)) {
      alert('Please select a valid video file.');
      return;
    }

    this.videoFile = file;
    this.videoSizeBytes = file.size || 0;
    if (this.videoUrl && this.videoUrl.startsWith('blob:')) URL.revokeObjectURL(this.videoUrl);
    this.videoUrl = URL.createObjectURL(file);

    this.videoPlayer.src = this.videoUrl;
    this.videoPlayer.load();

    this.videoPlayer.onloadedmetadata = () => {
      this.videoDuration = this.videoPlayer.duration || 10;
      this.startTime = 0;
      this.endTime = this.videoDuration;

      this.startTimeSlider.max = this.videoDuration.toString();
      this.endTimeSlider.max = this.videoDuration.toString();
      this.startTimeSlider.value = '0';
      this.endTimeSlider.value = this.videoDuration.toString();
      this.updateRangeDisplay();

      if (this.fileInfoName) this.fileInfoName.textContent = file.name;
      if (this.fileInfoMeta) this.fileInfoMeta.textContent = `${this.formatBytes(this.videoSizeBytes)} • ${this.formatTime(this.videoDuration)}`;

      this.updateSurfaceInfoBox();
      this.updateOverviewText();
      this.expandToOptionsWorkspace();
    };
  }

  private static extractYouTubeId(url: string): string | null {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  }

  private static async handleUrlLoad(inputUrl: string): Promise<void> {
    let targetUrl = inputUrl.trim().replace(/^http:\/\//i, 'https://');
    if (!targetUrl.startsWith('https://')) {
      targetUrl = 'https://' + targetUrl;
    }

    this.loadUrlBtn.disabled = true;
    this.loadUrlBtn.textContent = 'Resolving...';

    try {
      if (/tiktok\.com/i.test(targetUrl)) {
        const response = await fetch(`https://www.tikwm.com/api/?url=${encodeURIComponent(targetUrl)}`);
        const json = await response.json();
        if (json.code === 0 && json.data && json.data.play) {
          targetUrl = json.data.play.startsWith('http') ? json.data.play : `https://www.tikwm.com${json.data.play}`;
        } else {
          throw new Error(json.msg || 'Unable to parse TikTok video link');
        }
      } else if (/youtube\.com|youtu\.be/i.test(targetUrl)) {
        const ytId = this.extractYouTubeId(targetUrl);
        let resolved = false;

        if (ytId) {
          const invidiousInstances = [
            'https://inv.tux.pizza',
            'https://invidious.nerdvpn.de',
            'https://invidious.flokinet.to',
            'https://invidious.privacydev.net'
          ];
          for (const instance of invidiousInstances) {
            try {
              const res = await fetch(`${instance}/api/v1/videos/${ytId}`);
              if (!res.ok) continue;
              const data = await res.json();
              const mp4Format = data.formatStreams?.find((f: any) => f.container === 'mp4' || f.encoding === 'h264');
              if (mp4Format && mp4Format.url) {
                targetUrl = mp4Format.url;
                resolved = true;
                break;
              }
            } catch {}
          }
        }

        if (!resolved) {
          try {
            const res = await fetch('https://api.cobalt.tools/', {
              method: 'POST',
              headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
              body: JSON.stringify({ url: targetUrl })
            });
            const data = await res.json();
            if (data.url) {
              targetUrl = data.url;
              resolved = true;
            }
          } catch {}
        }

        if (!resolved) {
          throw new Error('Unable to extract stream from YouTube link. Please ensure link is public.');
        }
      }
    } catch (err: any) {
      alert(`Error loading video URL: ${err.message || 'Invalid or unsupported link'}`);
      this.loadUrlBtn.disabled = false;
      this.loadUrlBtn.textContent = 'Load';
      return;
    }

    this.videoFile = null;
    this.videoSizeBytes = 0;
    this.videoUrl = targetUrl;

    this.videoPlayer.crossOrigin = 'anonymous';
    this.videoPlayer.src = targetUrl;
    this.videoPlayer.load();

    this.videoPlayer.onloadedmetadata = () => {
      this.loadUrlBtn.disabled = false;
      this.loadUrlBtn.textContent = 'Load';
      this.videoDuration = this.videoPlayer.duration || 10;
      this.startTime = 0;
      this.endTime = this.videoDuration;

      this.startTimeSlider.max = this.videoDuration.toString();
      this.endTimeSlider.max = this.videoDuration.toString();
      this.startTimeSlider.value = '0';
      this.endTimeSlider.value = this.videoDuration.toString();
      this.updateRangeDisplay();

      if (this.fileInfoName) this.fileInfoName.textContent = 'Video Stream from URL';
      if (this.fileInfoMeta) this.fileInfoMeta.textContent = `Online Stream • ${this.formatTime(this.videoDuration)}`;

      this.updateSurfaceInfoBox();
      this.updateOverviewText();
      this.expandToOptionsWorkspace();
    };

    this.videoPlayer.onerror = () => {
      this.loadUrlBtn.disabled = false;
      this.loadUrlBtn.textContent = 'Load';
      alert('Failed to load video stream from URL. Please ensure link is accessible.');
    };
  }

  private static expandToOptionsWorkspace(): void {
    this.mainConverterCard.classList.add('expanded');
    this.initialWorkspaceWrap.style.display = 'none';
    this.expandedWorkspaceWrap.style.display = 'block';
  }

  private static resetToInitialWorkspace(): void {
    if (this.videoUrl && this.videoUrl.startsWith('blob:')) URL.revokeObjectURL(this.videoUrl);
    this.videoFile = null;
    this.videoUrl = null;
    this.videoDuration = 0;
    this.videoSizeBytes = 0;

    this.clearOutputState();

    this.mainConverterCard.classList.remove('expanded');
    this.expandedWorkspaceWrap.style.display = 'none';
    this.initialWorkspaceWrap.style.display = 'block';
    this.videoFileInput.value = '';
  }

  private static updateRangeDisplay(): void {
    if (this.rangeValText) this.rangeValText.textContent = `${this.startTime.toFixed(1)}s - ${this.endTime.toFixed(1)}s`;
  }

  private static async startSplitting(): Promise<void> {
    if (!this.videoFile && !this.videoUrl) return;
    if (this.isSplitting) return;

    this.isSplitting = true;
    this.extractBtn.disabled = true;
    this.extractBtnText.textContent = 'Splitting Video...';

    this.resultsContent.style.display = 'block';
    this.progressBarFill.style.width = '0%';
    this.progressText.textContent = 'Preparing video stream... (0%)';
    this.clipsGrid.innerHTML = '';

    const activeSurface = this.getActiveSurfaceObj();
    const effectiveClipSec = (this.selectedPlatform === 'custom') ? this.clipDurationSec : (activeSurface.maxSeconds || 60);

    try {
      this.splitClips = await SplitterEngine.splitVideo({
        videoFile: this.videoFile,
        videoUrl: this.videoUrl,
        totalDuration: this.videoDuration,
        clipDurationSec: effectiveClipSec,
        partCount: this.partCount,
        splitMode: this.splitMode,
        useTimeRange: this.useTimeRange,
        startTime: this.startTime,
        endTime: this.endTime,
        onProgress: (info: ProgressInfo | number) => {
          const percent = (typeof info === 'object') ? info.percent : info;
          const text = (typeof info === 'object' && info.text) ? info.text : `${percent}%`;
          this.progressBarFill.style.width = `${percent}%`;
          this.progressText.textContent = `${text.includes('%') ? text : `${text} (${percent}%)`}`;
        }
      });

      this.renderClipsList(this.splitClips);
    } catch (err: any) {
      alert(`Splitting error: ${err.message || 'Failed to process video clips'}`);
    }

    this.isSplitting = false;
    this.extractBtn.disabled = false;
    this.extractBtnText.textContent = 'Split Video';
  }

  private static renderClipsList(clips: SplitResultClip[]): void {
    this.clipsGrid.innerHTML = '';

    clips.forEach(clip => {
      const card = document.createElement('div');
      card.className = 'clip-item-card';

      card.innerHTML = `
        <div class="clip-thumb-box">
          ${clip.thumbnailUrl ? `<img src="${clip.thumbnailUrl}" class="clip-thumb-img" alt="${clip.name}">` : `<div class="clip-thumb-placeholder">🎬</div>`}
        </div>
        <div class="clip-item-info">
          <div class="clip-item-title">${clip.name}</div>
          <div class="clip-item-sub">
            <span class="clip-duration-badge">⏱️ ${clip.duration.toFixed(1)}s</span> (${this.formatTime(clip.start)} - ${this.formatTime(clip.end)}) • ${this.formatBytes(clip.blob.size)}
          </div>
        </div>
        <a href="${clip.url}" download="${clip.name}" class="clip-item-btn">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
          Download
        </a>
      `;

      this.clipsGrid.appendChild(card);
    });
  }

  private static async downloadAllZip(): Promise<void> {
    if (!this.splitClips || this.splitClips.length === 0) return;
    if (typeof (window as any).JSZip === 'undefined') {
      alert('JSZip library loading...');
      return;
    }

    const zip = new (window as any).JSZip();
    const folder = zip.folder('video_splits');

    this.splitClips.forEach(clip => {
      folder.file(clip.name, clip.blob);
    });

    this.downloadZipBtn.disabled = true;
    this.downloadZipBtn.textContent = 'Zipping Clips...';

    try {
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const zipUrl = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = zipUrl;
      a.download = `video_splits_${Date.now()}.zip`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(zipUrl), 10000);
    } catch (err) {
      alert('Error creating ZIP archive.');
    }

    this.downloadZipBtn.disabled = false;
    this.downloadZipBtn.innerHTML = `
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
        <polyline points="7 10 12 15 17 10"></polyline>
        <line x1="12" y1="15" x2="12" y2="3"></line>
      </svg>
      Download ZIP
    `;
  }

  private static downloadAllDirectFiles(): void {
    if (!this.splitClips || this.splitClips.length === 0) return;
    this.splitClips.forEach((clip, index) => {
      setTimeout(() => {
        const a = document.createElement('a');
        a.href = clip.url;
        a.download = clip.name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }, index * 350);
    });
  }

  private static async shareAllClips(): Promise<void> {
    if (!this.splitClips || this.splitClips.length === 0) return;

    try {
      const files = this.splitClips.map(clip => new File([clip.blob], clip.name, { type: clip.blob.type || 'video/mp4' }));

      if (navigator.canShare && navigator.canShare({ files: files })) {
        await navigator.share({
          files: files,
          title: `Split Video Clips for ${this.PLATFORM_CONFIGS[this.selectedPlatform]?.label || 'Social Media'}`,
          text: `Here are the split video clips generated by VideoSplitter!`
        });
      } else if (navigator.share) {
        await navigator.share({
          title: `VideoSplitter Clips`,
          text: `Check out these split video clips for ${this.selectedPlatform}`,
          url: window.location.href
        });
      } else {
        alert("Native direct file sharing is supported on mobile devices and supported browsers. Downloading all files directly...");
        this.downloadAllDirectFiles();
      }
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        alert(`Share notice: ${err.message || err}`);
      }
    }
  }

  private static clearOutputState(): void {
    this.splitClips.forEach(clip => {
      if (clip.url && clip.url.startsWith('blob:')) URL.revokeObjectURL(clip.url);
    });
    this.splitClips = [];
    if (this.resultsContent) this.resultsContent.style.display = 'none';
    if (this.clipsGrid) this.clipsGrid.innerHTML = '';
  }

  private static bindFaqAccordion(): void {
    const faqItems = document.querySelectorAll('.faq-item');
    faqItems.forEach(item => {
      const q = item.querySelector('.faq-question');
      if (q) {
        q.addEventListener('click', () => item.classList.toggle('active'));
      }
    });
  }
}
